import { getClientPublicIpSync } from './clientPublicIp';

/**
 * `fetch()` Request headers must be ISO-8859-1 (byte) values. Unicode (e.g. em dash, Arabic names, IDN URLs) throws
 * "String contains non ISO-8859-1 code point".
 */
function toFetchHeaderValue(value) {
  if (value == null || value === '') return '';
  let s = String(value);
  s = s
    .replace(/\u2014/g, '-') // em dash
    .replace(/\u2013/g, '-') // en dash
    .replace(/\u2026/g, '...') // ellipsis
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u00a0/g, ' ');
  let out = '';
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    out += c <= 255 ? s[i] : '?';
  }
  return out;
}

export function getDeviceType(userAgent) {
  if (!userAgent) return 'Desktop';
  if (/tablet|ipad/i.test(userAgent)) return 'Tablet';
  if (/mobile|android|iphone/i.test(userAgent)) return 'Mobile';
  return 'Desktop';
}

/**
 * Human-readable screen name from the pathname so SPA logging is not stuck on static document.title.
 */
export function humanizeRoutePath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return 'Home';
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return 'Home';
  const parts = p.replace(/^\//, '').split('/').filter(Boolean);
  return parts
    .map((seg) =>
      decodeURIComponent(seg)
        .split(/[-_]/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    )
    .join(' / ');
}

/**
 * Page label for activity headers / TrackActivity: route-based, optional h2.page-title when it adds detail.
 */
export function getPageLabelForActivity() {
  if (typeof window === 'undefined') return '';
  const pathLabel = humanizeRoutePath(window.location.pathname || '');
  const titleEl = document.querySelector('h2.page-title, h1.page-title, h4.page-title');
  let fromDom = '';
  if (titleEl && titleEl.textContent) fromDom = titleEl.textContent.trim();

  const docTitle = (document.title || '').trim();
  const isBoilerplateTitle =
    !docTitle ||
    /ainsoft delivery/i.test(docTitle) ||
    /#\s*1\s+delivery/i.test(docTitle) ||
    docTitle.length > 120;

  if (fromDom) {
    if (isBoilerplateTitle && fromDom === docTitle) return pathLabel;
    const domL = fromDom.toLowerCase();
    if (pathLabel.toLowerCase().includes(domL)) return pathLabel;
    return `${pathLabel} - ${fromDom}`;
  }

  if (docTitle && !isBoilerplateTitle) {
    const d = docTitle.toLowerCase();
    if (pathLabel.toLowerCase().includes(d)) return pathLabel;
    return `${pathLabel} - ${docTitle}`;
  }
  return pathLabel;
}

function visitorNameFromStorage() {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return '';
    const j = JSON.parse(raw);
    return (j.Name || j.UserName || j.userName || '').trim();
  } catch {
    return '';
  }
}

/**
 * Headers sent on API calls so the server can log page context (see Activity Tracker).
 * @param {string} [endpoint] API route name; Login skips extra headers.
 */
export function getActivityContextHeaders(endpoint) {
  if (typeof window === 'undefined') return {};
  const ep = String(endpoint || '').toLowerCase();
  if (ep.includes('login')) return {};

  const pageName = getPageLabelForActivity() || window.location.pathname || '';

  const ua = navigator.userAgent || '';
  const clientIp = getClientPublicIpSync();

  const h = {
    'X-Page-Url': toFetchHeaderValue(window.location.href),
    'X-Page-Name': toFetchHeaderValue(pageName),
    'X-Referrer': toFetchHeaderValue(document.referrer || ''),
    'X-Device-Name': toFetchHeaderValue(navigator.platform || ''),
    'X-Device-Type': toFetchHeaderValue(getDeviceType(ua)),
    'X-Visitor-Name': toFetchHeaderValue(visitorNameFromStorage()),
    'X-Computer-Name': toFetchHeaderValue(typeof navigator.appName === 'string' ? navigator.appName : ''),
  };
  if (clientIp) h['X-Client-Public-Ip'] = toFetchHeaderValue(clientIp);
  return h;
}
